import { StudentName } from "../../../shared-components/StudentName";
import React, { useEffect, useMemo, useState } from "react";
import { ImageUploadQRDialog } from "../../assignment/components/ImageUploadQRDialog";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Error } from "@mui/icons-material";

const SubmissionStatus = ({ submission }) => {
  const theme = useTheme();

  const Row = ({ children }) => (
    <Box display="flex" alignItems="flex-start">
      {children}
    </Box>
  );

  if (submission.submitted) {
    return (
      <Row>
        <CheckCircle
          sx={{ mr: 1, color: `${theme.palette.highlight} !important` }}
        />
        <Typography>Marked: {submission.percentage * 100}%</Typography>
      </Row>
    );
  }

  return (
    <Row>
      <Error sx={{ mr: 1, color: theme.palette.warning.main }} />
      <Typography>Not Submitted</Typography>
    </Row>
  );
};

export const QuizSubmissionsListItem = ({ submission, quizGroupId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Card sx={{ p: 1, backgroundColor: theme.palette.background.default }}>
      <CardContent sx={{ p: 0.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Student info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexBasis: 250, // minimum width
              flexGrow: 2.5, // expands proportionally
              minWidth: 150, // never shrink too much
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar>
                {(submission.student?.identifier || "?").toString().charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5">
                  <StudentName student={submission.student} />
                </Typography>
                {submission.submitted ? (
                  <Typography variant="body1" color="text.secondary">
                    Submitted{" "}
                    {formatDistanceToNow(new Date(submission.submittedAt), {
                      addSuffix: true,
                    })}
                  </Typography>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Not Submitted
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          {/* Viewed / Not Viewed */}
          <Box
            sx={{
              flexBasis: 100,
              flexGrow: 1,
              minWidth: 80,
            }}
          >
            <Chip
              label={submission.submitted ? "Submitted" : "Not Submitted"}
              color={submission.submitted ? "success" : "default"}
            />
          </Box>

          {/* Answer Status */}
          <Box
            sx={{
              flexBasis: 150,
              flexGrow: 1.5,
              minWidth: 120,
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <SubmissionStatus submission={submission} />
          </Box>

          {/* View button */}
          <Box
            sx={{
              flexBasis: 100,
              flexGrow: 1,
              minWidth: 80,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={!submission.submitted}
              onClick={() =>
                navigate(
                  `/quizzes/submission/view/${quizGroupId}/${submission.student.id}`,
                )
              }
            >
              View
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const QuizGroupSubmissions = ({ quizGroup, submissions }) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadUrl, setUploadUrl] = useState(null);

  const ApiClient = useApiClient();

  const [submissionsStatus, setSubmissionsStatus] = useState("all");
  const theme = useTheme();

  const totalCount = submissions?.length || 0;

  const submittedCount = useMemo(
    () => (submissions ? submissions.filter((s) => s.submitted).length : 0),
    [submissions],
  );
  const notSubmittedCount = totalCount - submittedCount;

  useEffect(() => {
    if (totalCount === 0 && submissionsStatus !== "not_submitted") {
      setSubmissionsStatus("all");
    }
  }, [totalCount, submissionsStatus]);

  const filteredSubmissions = useMemo(() => {
    if (submissionsStatus === "all") return submissions;
    if (submissionsStatus === "not_submitted")
      return submissions?.filter((s) => !s.submitted) || [];
    if (submissionsStatus === "submitted")
      return submissions?.filter((s) => s.submitted) || [];
    return submissions;
  }, [submissions, submissionsStatus]);

  const handleGenerateUrlClicked = async () => {
    setUploadUrl(null);
    try {
      const response = await ApiClient.quiz.generateQuizGroupUploadUrl({
        quizGroup: quizGroup.id, // TODO
      });
      if (response.success) {
        setUploadUrl(response.url);
        setUploadDialogOpen(true);
      } else {
        enqueueSnackbar("Error Generating Upload URL- please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to Generate Upload URL", error);
      enqueueSnackbar("Error Generating Upload URL- please try again!", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <ImageUploadQRDialog
        url={uploadUrl}
        open={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
        quiz
      />
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h5">Student Submissions</Typography>
          <Button
            variant="outlined"
            startIcon={<FruitifyIcon name={"Camera"} />}
            sx={{
              textTransform: "none",
              paddingX: 3,
              paddingY: 1.5,
            }}
            onClick={() => handleGenerateUrlClicked()}
          >
            Get Submission QR code
          </Button>
        </Stack>
        <ToggleButtonGroup
          sx={{ mt: 2, width: "100%" }}
          value={submissionsStatus}
          exclusive
          onChange={(_, newSubmissionsStatus) =>
            setSubmissionsStatus(newSubmissionsStatus)
          }
          aria-label="Submissions Status"
        >
          <ToggleButton sx={{ flex: 1 }} value="all">
            All Submissions
          </ToggleButton>
          <ToggleButton
            sx={{ flex: 1 }}
            value="not_submitted"
            disabled={notSubmittedCount === 0}
          >
            Not Submitted
          </ToggleButton>
          <ToggleButton
            sx={{ flex: 1 }}
            value="submitted"
            disabled={submittedCount === 0}
          >
            Submitted
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack
          direction={"column"}
          spacing={2}
          sx={{ mt: 2, flex: 1, minHeight: 0 }}
        >
          {filteredSubmissions && filteredSubmissions.length > 0 && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: 400,
                overflowY: "auto",
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                p: 1,
                scrollbarGutter: "stable",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            >
              <Stack direction="column" spacing={1}>
                {filteredSubmissions.map((submission, idx) => (
                  <QuizSubmissionsListItem
                    key={idx}
                    submission={submission}
                    quizGroupId={quizGroup.id}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
